/**
 * common/errors.js — one error type for the whole API surface.
 * Every failure (network, timeout, HTTP 4xx/5xx, mock) is normalized to ApiError
 * so callers can branch on `.status` / `.code` without guessing shapes.
 */

export const ErrorCodes = {
  NETWORK: "network_error",
  TIMEOUT: "timeout",
  UNAUTHORIZED: "unauthorized",
  FORBIDDEN: "forbidden",
  NOT_FOUND: "not_found",
  VALIDATION: "validation_error",
  RATE_LIMIT: "rate_limited",
  INSUFFICIENT_CREDITS: "insufficient_credits",
  SERVER: "server_error",
  UNKNOWN: "unknown_error",
};

export class ApiError extends Error {
  constructor(message, { status = 0, code = ErrorCodes.UNKNOWN, details = null } = {}) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.details = details;
  }
  get isAuth() { return this.status === 401 || this.status === 403; }
  get isNetwork() { return this.status === 0; }
  toJSON() { return { name: this.name, message: this.message, status: this.status, code: this.code, details: this.details }; }
}

/** Map an HTTP status to a stable error code. */
export function codeForStatus(status) {
  switch (status) {
    case 400: return ErrorCodes.VALIDATION;
    case 401: return ErrorCodes.UNAUTHORIZED;
    case 403: return ErrorCodes.FORBIDDEN;
    case 404: return ErrorCodes.NOT_FOUND;
    case 402: return ErrorCodes.INSUFFICIENT_CREDITS;
    case 429: return ErrorCodes.RATE_LIMIT;
    default: return status >= 500 ? ErrorCodes.SERVER : ErrorCodes.UNKNOWN;
  }
}

/** Wrap any thrown value as an ApiError. */
export function normalizeError(e) {
  if (e instanceof ApiError) return e;
  if (e && e.name === "AbortError") return new ApiError("Request timed out", { code: ErrorCodes.TIMEOUT });
  return new ApiError(e && e.message ? e.message : "Network error", { code: ErrorCodes.NETWORK });
}

export default ApiError;
